import React, { useState, useEffect } from 'react';
import { AppBar, Toolbar, Box, Container, IconButton, Avatar, Badge, Tooltip, Typography, Button, Menu, MenuItem } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ChatIcon from '@mui/icons-material/Chat';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

const Navbar = () => {
  const navigate = useNavigate();
  const { unreadCount: unreadNotificationCount } = useNotifications();
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const { currentUser, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      handleMenuClose();
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Get the first letter of the user's name for the avatar
  const getInitial = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName.charAt(0).toUpperCase();
    }
    if (currentUser?.email) {
      return currentUser.email.charAt(0).toUpperCase();
    }
    return 'U';
  };
  
  return (
    <AppBar 
      position="fixed" 
      elevation={0}
      sx={{ 
        height: '77px',
        backgroundColor: '#FFE500',
        top: 0,
        left: 0,
        right: 0,
        width: '100%',
        margin: 0,
      }}
    >
      <Container 
        maxWidth={false} 
        disableGutters 
        sx={{
          height: '100%',
          margin: 0,
          padding: 0,
          maxWidth: 'none',
        }}
      >
        <Toolbar 
          sx={{ 
            minHeight: '77px !important',
            padding: '0 24px !important',
            margin: 0,
            height: '100%',
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Link to="/" style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}>
              <img 
                src={`${process.env.PUBLIC_URL}/fixmigo-navbar.png`} 
                alt="Fixmigo Logo" 
                style={{ 
                  height: '40px',
                  marginRight: '8px'
                }}
              />
            </Link>
          </Box>
          
          {currentUser ? (
            // Authenticated user view
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, ml: 'auto', alignItems: 'center' }}>
              <Tooltip title="Mis Reservas">
                <IconButton
                  size="large"
                  color="inherit"
                  sx={{
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                    mr: { xs: 0, sm: 1 },
                    padding: { xs: '6px', sm: '8px' }
                  }}
                  onClick={() => navigate('/bookings')}
                >
                  <CalendarMonthIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                </IconButton>
              </Tooltip>
              
              {/* Messages icon with consolidated notifications */}
              <Tooltip title="Mis Mensajes y Notificaciones">
                <IconButton
                  onClick={() => navigate('/messages')}
                  sx={{
                    color: '#333',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    },
                    padding: { xs: '6px', sm: '8px' }
                  }}
                >
                  <Badge 
                    color="error" 
                    badgeContent={unreadNotificationCount} 
                    overlap="circular"
                    invisible={unreadNotificationCount === 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: '18px', sm: '20px' },
                        minWidth: { xs: '18px', sm: '20px' }
                      }
                    }}
                  >
                    <ChatIcon sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                  </Badge>
                </IconButton>
              </Tooltip>
              
              {/* Profile Avatar */}
              <Tooltip title="Mi Perfil">
                <IconButton
                  onClick={handleProfileMenuClick}
                  sx={{
                    p: { xs: 0.3, sm: 0.5 },
                    border: '2px solid transparent',
                    transition: 'all 0.2s',
                    '&:hover': {
                      border: '2px solid #FFE500',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Avatar 
                    src={currentUser.photoURL}
                    sx={{ 
                      width: { xs: 32, sm: 40 }, 
                      height: { xs: 32, sm: 40 }, 
                      bgcolor: currentUser.photoURL ? 'transparent' : '#FFE500',
                      color: '#222222',
                      fontWeight: 'bold',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {!currentUser.photoURL && getInitial()}
                  </Avatar>
                </IconButton>
              </Tooltip>
              
              {/* Profile Menu */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <MenuItem onClick={() => { handleMenuClose(); navigate('/profile'); }}>
                  Mi Perfil
                </MenuItem>
                {currentUser.role === 'provider' && (
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/provider-dashboard'); }}>
                    Panel de Proveedor
                  </MenuItem>
                )}
                <MenuItem onClick={handleLogout}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                  Cerrar Sesión
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            // Non-authenticated user view
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center' }}>
              <Button 
                variant="outlined" 
                color="inherit" 
                onClick={() => navigate('/register')}
                sx={{ 
                  borderColor: '#333',
                  color: '#333',
                  '&:hover': {
                    borderColor: '#000',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1.25, sm: 2 }
                }}
              >
                Registrarse
              </Button>
              <Button 
                variant="contained" 
                onClick={() => navigate('/login')}
                sx={{ 
                  backgroundColor: '#333',
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: '#000',
                  },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  py: { xs: 0.5, sm: 0.75 },
                  px: { xs: 1.25, sm: 2 }
                }}
              >
                Iniciar Sesión
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar; 